import { z } from 'zod';
import { paginationQuery } from '../api.js';
/**
 * Marketing (KON-170, siehe docs/marketing.md): Newsletter-Abonnenten mit nachweisbarer Einwilligung (Double-Opt-In), Kampagnen an einen Verteiler aus den Kontakten
 * und die Auswertung des Versands. Es gibt bewusst kein Öffnungs- und Klick-Tracking.
 */
// ---------------------------------------------------------------------------
// Abonnenten
// ---------------------------------------------------------------------------
export const SUBSCRIPTION_STATUSES = ['pending', 'subscribed', 'unsubscribed'];
export const SUBSCRIPTION_STATUS_LABELS = { pending: 'Bestätigung offen', subscribed: 'Angemeldet', unsubscribed: 'Abgemeldet' };
export const SUBSCRIPTION_SOURCES = ['backoffice', 'website'];
export const SUBSCRIPTION_SOURCE_LABELS = { backoffice: 'Backoffice', website: 'Webseite' };
export const listSubscriptionsQuery = paginationQuery.extend({
    q: z.string().trim().max(100).optional(),
    status: z.enum(SUBSCRIPTION_STATUSES).optional(),
});
/**
 * Einen Kontakt als Abonnent anlegen. `invite`: Der Kontakt bekommt eine E-Mail mit Bestätigungslink (Double-Opt-In), erst danach bekommt er Kampagnen.
 * `confirmed`: Die Einwilligung liegt schon vor (zum Beispiel mündlich oder auf Papier). Dann muss der Nachweis dabei stehen.
 */
export const createSubscriptionInput = z
    .object({
    contactId: z.uuid('Bitte einen Kontakt wählen'),
    mode: z.enum(['invite', 'confirmed']),
    consentNote: z.string().trim().max(500).optional().transform((v) => (v ? v : undefined)),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.mode === 'confirmed' && !v.consentNote)
        ctx.addIssue({ code: 'custom', path: ['consentNote'], message: 'Bitte notieren Sie, wie und wann die Einwilligung erteilt wurde.' });
});
export const unsubscribeInput = z
    .object({ reason: z.string().trim().max(200).optional().transform((v) => (v ? v : undefined)) })
    .strict();
/** Anmeldung über die Kundenwebseite (API-Schlüssel mit dem Recht `marketing:subscribe`): immer mit Bestätigungsmail */
export const websiteSubscribeInput = z
    .object({
    name: z.string().trim().min(2, 'Bitte einen Namen angeben').max(120),
    email: z.string().trim().toLowerCase().max(200).pipe(z.email('Ungültige E-Mail-Adresse')),
    /** Der Besucher hat das Häkchen gesetzt */
    consent: z.literal(true, 'Die Einwilligung fehlt'),
    /** Wortlaut des Häkchens auf der Webseite, als Nachweis */
    consentText: z.string().trim().max(500).optional().transform((v) => (v ? v : undefined)),
})
    .strict();
// ---------------------------------------------------------------------------
// Kampagnen
// ---------------------------------------------------------------------------
export const CAMPAIGN_STATUSES = ['draft', 'sending', 'sent', 'cancelled'];
export const CAMPAIGN_STATUS_LABELS = { draft: 'Entwurf', sending: 'Wird versendet', sent: 'Versendet', cancelled: 'Abgebrochen' };
/** Platzhalter im Text einer Kampagne */
export const CAMPAIGN_PLACEHOLDERS = [
    { key: '{{name}}', label: 'Name des Empfängers' },
    { key: '{{firma}}', label: 'Name Ihres Unternehmens' },
];
/** Wer eine Kampagne bekommt: angemeldete Abonnenten, deren Kontakt auf alle gesetzten Bedingungen passt. Leere Bedingung heißt „egal“. */
export const campaignAudience = z
    .object({
    relationTypeIds: z.array(z.uuid()).max(50).default([]),
    kinds: z.array(z.enum(['person', 'company'])).max(2).default([]),
    postalCodePrefix: z.string().trim().max(5, 'Höchstens 5 Ziffern').regex(/^\d*$/, 'Nur Ziffern').optional().transform((v) => (v ? v : undefined)),
})
    .strict();
/**
 * Alle Kontakte, die noch keinen Abonnenteneintrag haben (auch keine Abmeldung), auf einmal aufnehmen, optional eingegrenzt wie ein Verteiler. Wer sich je abgemeldet hat,
 * ist ein Eintrag und wird nie übernommen. `invite` verschickt Bestätigungsmails im Hintergrund, `confirmed` setzt die Anmeldung mit dem gemeinsamen Nachweis.
 * `acknowledged` hält fest, dass der Benutzer die rechtliche Verantwortung gelesen hat.
 */
export const bulkSubscribeInput = z
    .object({
    mode: z.enum(['invite', 'confirmed']),
    consentNote: z.string().trim().max(500).optional().transform((v) => (v ? v : undefined)),
    audience: campaignAudience.default({ relationTypeIds: [], kinds: [], postalCodePrefix: undefined }),
    acknowledged: z.literal(true, 'Bitte bestätigen Sie den Hinweis'),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.mode === 'confirmed' && !v.consentNote)
        ctx.addIssue({ code: 'custom', path: ['consentNote'], message: 'Bitte notieren Sie, wie und wann die Einwilligung erteilt wurde.' });
});
const name = z.string().trim().min(2, 'Bitte einen Namen angeben').max(120);
const subject = z.string().trim().min(2, 'Bitte einen Betreff angeben').max(200);
const body = z.string().trim().min(1, 'Bitte einen Text schreiben').max(20_000, 'Höchstens 20.000 Zeichen');
export const createCampaignInput = z.object({ name, subject, body, audience: campaignAudience.default({ relationTypeIds: [], kinds: [], postalCodePrefix: undefined }) }).strict();
export const updateCampaignInput = z
    .object({ name: name.optional(), subject: subject.optional(), body: body.optional(), audience: campaignAudience.optional() })
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const listCampaignsQuery = paginationQuery.extend({ status: z.enum(CAMPAIGN_STATUSES).optional() });
export const testSendInput = z.object({ to: z.string().trim().toLowerCase().max(200).pipe(z.email('Ungültige E-Mail-Adresse')) }).strict();
export const RECIPIENT_STATUSES = ['queued', 'sent', 'failed', 'skipped'];
export const RECIPIENT_STATUS_LABELS = { queued: 'Wartet', sent: 'Gesendet', failed: 'Fehlgeschlagen', skipped: 'Nicht gesendet' };
export const listRecipientsQuery = paginationQuery.extend({ status: z.enum(RECIPIENT_STATUSES).optional() });
//# sourceMappingURL=marketing.js.map